import XLSX from 'xlsx-js-style';
import { toast } from 'sonner';

export const getStatusLabel = (status) => {
  const statusMap = {
    pending_approval: 'Pending Function Head Approval',
    approved: 'PO Issued To Vendor',
    rejected: 'Rejected',
    pending_finance_approval: 'Pending Finance Approval',
    tax_invoice_uploaded: 'Pending Finance Approval',
    additional_info_requested: 'Addl Info Requested',
    finance_approved: 'Approved For Payment',
    paid: 'Paid',
    po_generated: 'Pending Accounts Approval'
  };
  return statusMap[status] || status || '';
};

export const toDateOnly = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

export const getTdsAmount = (pr) => {
  if (pr.is_tds_applicable !== 'Yes') return 0;
  const amount = (pr.base_amount || 0) * ((pr.tds_percentage || 0) / 100);
  return Math.round(amount * 100) / 100;
};

export const getPaymentDetails = (pr) => {
  const payments = [];

  if (pr.advance_utr_number) {
    payments.push({ utr: pr.advance_utr_number, date: pr.advance_paid_at });
  }

  if (pr.partial_payments?.length) {
    pr.partial_payments.forEach((payment) => {
      payments.push({ utr: payment.utr_number || '', date: payment.paid_at });
    });
  } else if (pr.utr_number) {
    payments.push({ utr: pr.utr_number, date: pr.paid_at });
  }

  return {
    utrs: payments.map((p) => p.utr).filter(Boolean).join('\n'),
    dates: payments.map((p) => toDateOnly(p.date)).filter(Boolean).join('\n')
  };
};

export const getPendingActionAt = (pr, roleEmails) => {
  if (pr.hold_status) return pr.held_by || '';
  if (pr.status === 'paid' || pr.status === 'rejected') return '';

  if (pr.advance_payment_status === 'pending_ppa') {
    return (roleEmails.finance_head || []).join(', ');
  }
  if (pr.advance_payment_status === 'approved_pp') {
    return (roleEmails.finance_team || []).join(', ');
  }

  switch (pr.status) {
    case 'pending_approval':
      return pr.function_approver || '';
    case 'approved':
      return pr.requested_by || '';
    case 'pending_finance_approval':
    case 'tax_invoice_uploaded':
      return (roleEmails.finance_head || []).join(', ');
    case 'additional_info_requested':
      return pr.requested_by || '';
    case 'finance_approved':
      return (roleEmails.finance_team || []).join(', ');
    default:
      return '';
  }
};

export const applyExportFilters = (requests, filters) => {
  let filtered = requests;

  if (filters.entities?.length) {
    filtered = filtered.filter((pr) => filters.entities.includes(pr.entity_id));
  }

  if (filters.vendors?.length) {
    filtered = filtered.filter((pr) => filters.vendors.includes(pr.vendor_id));
  }

  if (filters.costCenters?.length) {
    filtered = filtered.filter((pr) => filters.costCenters.includes(pr.cost_centre));
  }

  if (filters.statuses?.length) {
    filtered = filtered.filter((pr) => {
      if (filters.statuses.includes('on_hold') && pr.hold_status) return true;
      return filters.statuses.includes(pr.status);
    });
  }

  if (filters.createdFrom) {
    filtered = filtered.filter((pr) => toDateOnly(pr.created_at) >= filters.createdFrom);
  }

  if (filters.createdTo) {
    filtered = filtered.filter((pr) => toDateOnly(pr.created_at) <= filters.createdTo);
  }

  if (filters.invoiceDateFrom) {
    filtered = filtered.filter((pr) => {
      const invoiceDate = toDateOnly(pr.tax_invoice_date);
      return invoiceDate && invoiceDate >= filters.invoiceDateFrom;
    });
  }

  if (filters.invoiceDateTo) {
    filtered = filtered.filter((pr) => {
      const invoiceDate = toDateOnly(pr.tax_invoice_date);
      return invoiceDate && invoiceDate <= filters.invoiceDateTo;
    });
  }

  return filtered;
};

export const validateExportFilters = (filters) => {
  if (filters.createdFrom && filters.createdTo && filters.createdFrom > filters.createdTo) {
    return 'Created From date must be before or equal to Created To date';
  }
  if (filters.invoiceDateFrom && filters.invoiceDateTo && filters.invoiceDateFrom > filters.invoiceDateTo) {
    return 'Invoice Date From must be before or equal to Invoice Date To';
  }
  return null;
};

export const buildAndDownloadInvoiceExport = (requests, vendorsById, roleEmails) => {
  if (!requests.length) {
    toast.error('No invoices available to export');
    return false;
  }

  const sheetHeaders = [
    'Vendor Name',
    'Entity Name',
    'Cost Center',
    'Invoice Date',
    'Due Date',
    'PO Number',
    'Status',
    'Pending Action at',
    'TDS Percentage',
    'Base Amount',
    'Tax Amount',
    'Total Amount',
    'TDS Amount',
    'Payable Amount',
    'Total Paid',
    'Remark',
    'UTR No.',
    'Payment Date'
  ];

  const rows = requests.map((pr) => {
    const vendor = vendorsById.get(pr.vendor_id);
    const vendorName = vendor?.legal_entity_name || vendor?.trade_name || 'N/A';
    const { utrs, dates } = getPaymentDetails(pr);
    return [
      vendorName,
      pr.entity_name || 'N/A',
      pr.cost_centre || '',
      toDateOnly(pr.tax_invoice_date),
      toDateOnly(pr.due_date),
      pr.po_number || '',
      getStatusLabel(pr.status),
      getPendingActionAt(pr, roleEmails),
      pr.is_tds_applicable === 'Yes' ? (pr.tds_percentage || 0) : 0,
      pr.base_amount || 0,
      pr.tax_amount || 0,
      pr.total_amount || pr.base_amount || 0,
      getTdsAmount(pr),
      pr.payable_amount || pr.total_amount || pr.base_amount || 0,
      pr.total_paid_amount || 0,
      pr.reason || '',
      utrs,
      dates
    ];
  });

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([sheetHeaders, ...rows]);
  worksheet['!cols'] = [
    { wch: 28 },
    { wch: 28 },
    { wch: 18 },
    { wch: 14 },
    { wch: 14 },
    { wch: 18 },
    { wch: 28 },
    { wch: 28 },
    { wch: 16 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 16 },
    { wch: 14 },
    { wch: 30 },
    { wch: 22 },
    { wch: 22 }
  ];

  sheetHeaders.forEach((_, index) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
    if (!worksheet[cellAddress]) return;
    worksheet[cellAddress].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '1F4E78' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: 'D9D9D9' } },
        bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
        left: { style: 'thin', color: { rgb: 'D9D9D9' } },
        right: { style: 'thin', color: { rgb: 'D9D9D9' } }
      }
    };
  });

  const datePart = new Date().toISOString().split('T')[0];
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');
  XLSX.writeFile(workbook, `invoices_export_${datePart}.xlsx`);
  toast.success('Invoice export downloaded');
  return true;
};
